import { useState, type ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import sql from "react-syntax-highlighter/dist/esm/languages/prism/sql";
import json from "react-syntax-highlighter/dist/esm/languages/prism/json";
import python from "react-syntax-highlighter/dist/esm/languages/prism/python";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import { Check, Copy } from "lucide-react";
import { cn } from "@/lib/utils";

SyntaxHighlighter.registerLanguage("sql", sql);
SyntaxHighlighter.registerLanguage("json", json);
SyntaxHighlighter.registerLanguage("python", python);

const SUPPORTED = new Set(["sql", "json", "python"]);

export function CopyButton({ text, className, label = "Kopiëren" }: { text: string; className?: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => undefined,
    );
  };
  return (
    <button
      type="button"
      onClick={copy}
      className={cn(
        "inline-flex items-center gap-1 rounded border border-border bg-card px-1.5 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground",
        className,
      )}
      aria-label={label}
      title={label}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Gekopieerd" : null}
    </button>
  );
}

/** Light-theme syntax-highlighted code block with copy button and optional title bar. */
export function CodeBlock({
  code,
  language = "sql",
  title,
  actions,
  className,
  maxHeight = 420,
  wrap = true,
}: {
  code: string;
  language?: string;
  title?: ReactNode;
  actions?: ReactNode;
  className?: string;
  maxHeight?: number | string;
  wrap?: boolean;
}) {
  const lang = SUPPORTED.has(language) ? language : "text";
  return (
    <div className={cn("group relative overflow-hidden rounded-md border border-border bg-sl-grey-50", className)}>
      {title ? (
        <div className="flex items-center justify-between gap-2 border-b border-border bg-card px-3 py-1.5">
          <span className="text-xs font-semibold text-muted-foreground">{title}</span>
          <div className="flex items-center gap-1.5">
            {actions}
            <CopyButton text={code} />
          </div>
        </div>
      ) : (
        <CopyButton text={code} className="absolute right-2 top-2 z-10 opacity-0 focus:opacity-100 group-hover:opacity-100" />
      )}
      <SyntaxHighlighter
        language={lang}
        style={oneLight}
        wrapLongLines={wrap}
        customStyle={{ margin: 0, padding: "0.75rem 1rem", background: "transparent", fontSize: "0.8rem", lineHeight: 1.6, maxHeight, overflow: "auto" }}
        codeTagProps={{ style: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" } }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
}

/** Markdown renderer for agent answers; fenced code blocks are highlighted with CodeBlock. */
export function Markdown({ children, className }: { children: string; className?: string }) {
  return (
    <div className={cn("text-sm leading-relaxed break-words text-foreground", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="my-2 first:mt-0 last:mb-0">{children}</p>,
          a: ({ children, href }) => (
            <a href={href} target="_blank" rel="noreferrer" className="font-medium underline underline-offset-2">
              {children}
            </a>
          ),
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          ul: ({ children }) => <ul className="my-2 list-disc space-y-1 pl-5">{children}</ul>,
          ol: ({ children }) => <ol className="my-2 list-decimal space-y-1 pl-5">{children}</ol>,
          h1: ({ children }) => <h3 className="mt-4 mb-1 text-base font-semibold first:mt-0">{children}</h3>,
          h2: ({ children }) => <h3 className="mt-4 mb-1 text-base font-semibold first:mt-0">{children}</h3>,
          h3: ({ children }) => <h4 className="mt-3 mb-1 text-sm font-semibold first:mt-0">{children}</h4>,
          h4: ({ children }) => <h4 className="mt-3 mb-1 text-sm font-semibold first:mt-0">{children}</h4>,
          blockquote: ({ children }) => <blockquote className="my-2 border-l-2 border-sl-yellow pl-3 text-muted-foreground">{children}</blockquote>,
          hr: () => <hr className="my-3 border-border" />,
          table: ({ children }) => (
            <div className="my-3 overflow-x-auto rounded-md border border-border">
              <table className="w-full border-collapse text-xs">{children}</table>
            </div>
          ),
          th: ({ children }) => <th className="border-b border-border bg-sl-grey-50 px-2.5 py-1.5 text-left font-semibold">{children}</th>,
          td: ({ children }) => <td className="border-b border-border px-2.5 py-1.5 align-top tabular-nums">{children}</td>,
          pre: ({ children }) => <>{children}</>,
          code: ({ className, children }) => {
            const text = String(children ?? "").replace(/\n$/, "");
            const lang = /language-(\w+)/.exec(className || "")?.[1];
            if (lang || text.includes("\n")) return <CodeBlock code={text} language={lang ?? "sql"} className="my-3" />;
            return <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]">{children}</code>;
          },
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
